import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Pricing', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Why Us', href: '#pricing' },
    { name: 'FAQ', href: '#faq' },
  ];

  const whatsappLink = "https://wa.me/919353316112?text=Hi%20LitheForce!%20I'm%20interested%20in%20your%20AI%20solutions.";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-header shadow-sm' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1a237e] to-[#00e5ff] flex items-center justify-center">
              <span className="text-white font-bold text-sm">LF</span>
            </div>
            <span className="font-semibold text-xl text-[#1a237e]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              LitheForce
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-[#5a6b8c] hover:text-[#1a237e] font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button
                className="btn-primary flex items-center gap-2"
                data-testid="header-cta-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Book Consultation
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#1a237e]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-slate-100 mobile-menu-enter"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col py-4" aria-label="Mobile navigation">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="px-4 py-3 text-[#5a6b8c] hover:text-[#1a237e] hover:bg-slate-50 font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                ))}
                <div className="px-4 pt-4">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="btn-primary w-full flex items-center justify-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Book Consultation
                    </Button>
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
